/**
 * Geocodificador especializado para Estaciones de Servicio de Andalucía
 * 
 * Conecta con la API REST del Ministerio para la Transición Ecológica (MITECO):
 * - Gasolineras convencionales
 * - Electrolineras (carga eléctrica)
 * - GLP/GNC (gas licuado/comprimido)
 * 
 * Fuentes oficiales:
 * - REST MITECO: https://sedeaplicaciones.minetur.gob.es/ServiciosRESTCarburantes/PreciosCarburantes/
 * 
 * Tipología PTEL: GASOLINERAS, ELECTROLINERAS, COMBUSTIBLE
 * 
 * @module services/geocoding/specialized
 * @version 1.0.0 - F2.T8
 */

import type { AxiosInstance, AxiosError } from 'axios';
import axios from 'axios';
import proj4 from 'proj4';
import { FuzzySearchEngine, type SearchableItem } from '../../search';
import type { GeocodingResult as LegacyGeocodingResult, SpecializedGeocoderConfig } from '../../../types/infrastructure';
import type { GeocodingResult as TypedResult } from '../../../types/errors';
import { 
  createErrorContext, 
  createError, 
  success, 
  failure,
} from '../../../types/errors';
import type { GeocodedCoordinates } from '../generic/CartoCiudadGeocoder';
import type { FuelStationAPIResponse, FuelStationAPIRecord } from '../../../types/geojson';

// Definir proyecciones
const WGS84 = 'EPSG:4326';
const UTM30 = '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs';

/**
 * Tipos de estaciones de servicio
 */
export enum FuelStationType {
  /** Gasolinera convencional */
  GASOLINERA = 'GASOLINERA',
  /** Estación con GLP (Gas Licuado de Petróleo) */
  GLP = 'GLP',
  /** Estación con GNC (Gas Natural Comprimido) */
  GNC = 'GNC',
  /** Estación con GNL (Gas Natural Licuado) */
  GNL = 'GNL',
  /** Electrolinera / Punto de carga eléctrica */
  ELECTROLINERA = 'ELECTROLINERA',
  /** Estación con hidrógeno */
  HIDROGENO = 'HIDROGENO',
  /** Estación multiservicio */
  MULTISERVICIO = 'MULTISERVICIO'
}

/**
 * Feature de estación de servicio parseada
 */
export interface FuelStationFeature {
  /** ID único MITECO */
  id: string;
  /** Nombre/Rótulo comercial */
  name: string;
  /** Coordenada X UTM30 ETRS89 */
  x: number;
  /** Coordenada Y UTM30 ETRS89 */
  y: number;
  /** Municipio */
  municipality?: string;
  /** Provincia */
  province?: string;
  /** Dirección completa */
  address?: string;
  /** Propiedades adicionales */
  properties: {
    /** Tipo de estación */
    type: FuelStationType;
    /** Rótulo comercial */
    rotulo?: string;
    /** Código postal */
    postalCode?: string;
    /** Horario */
    horario?: string;
    /** Tipo de venta (P=Público, R=Restringido) */
    tipoVenta?: string;
    /** Tiene GLP */
    hasGLP?: boolean;
    /** Tiene GNC */
    hasGNC?: boolean;
    /** Tiene GNL */
    hasGNL?: boolean;
    /** Tiene carga eléctrica */
    hasElectric?: boolean;
    /** Tiene hidrógeno */
    hasHydrogen?: boolean;
    /** Margen de carretera (D=Derecho, I=Izquierdo) */
    margen?: string;
    /** Localidad */
    localidad?: string;
    /** Código INE municipio */
    idMunicipio?: string;
    /** Latitud original WGS84 */
    latWGS84?: number;
    /** Longitud original WGS84 */
    lonWGS84?: number;
  };
}

/**
 * Opciones de búsqueda para estaciones de servicio
 */
export interface FuelStationSearchOptions {
  /** Nombre a buscar */
  name: string;
  /** Municipio para filtrar */
  municipality?: string;
  /** Provincia para filtrar */
  province?: string;
  /** Código INE municipio (5 dígitos) */
  muniCode?: string;
  /** Código provincia (2 dígitos) */
  provinceCode?: string;
  /** Tipo de estación */
  stationType?: FuelStationType;
  /** Límite de resultados */
  maxResults?: number;
}

/**
 * Códigos de provincia de Andalucía (INE)
 */
const ANDALUCIA_PROVINCE_CODES: Record<string, string> = {
  'almería': '04', 'almeria': '04',
  'cádiz': '11', 'cadiz': '11',
  'córdoba': '14', 'cordoba': '14',
  'granada': '18',
  'huelva': '21',
  'jaén': '23', 'jaen': '23',
  'málaga': '29', 'malaga': '29',
  'sevilla': '41'
};

/**
 * ID CCAA Andalucía según API MITECO
 */
const CCAA_ANDALUCIA = '01';

/**
 * Geocodificador especializado para estaciones de servicio andaluzas
 * 
 * Precisión esperada: ±10-25m (coordenadas WGS84 de MITECO)
 * Cobertura: ~2,500 estaciones en Andalucía
 * 
 * @example
 * ```typescript
 * const geocoder = new GasolinerasGeocoder();
 * const result = await geocoder.geocode({
 *   name: 'Repsol Autovía A-92',
 *   municipality: 'Granada',
 *   province: 'Granada'
 * });
 * ```
 */
export class GasolinerasGeocoder {
  protected config: SpecializedGeocoderConfig;
  protected axiosInstance: AxiosInstance;
  protected featureCache: Map<string, FuelStationFeature[]>;
  
  /** Motor de búsqueda fuzzy con pre-indexación */
  protected searchEngine: FuzzySearchEngine;
  
  /** Provincia actualmente indexada */
  protected currentIndexProvince: string | null = null;
  
  /** Features indexadas actualmente */
  protected indexedFeatures: FuelStationFeature[] = [];

  constructor(config?: Partial<SpecializedGeocoderConfig>) {
    this.config = {
      ...this.getDefaultConfig(),
      ...config
    };

    this.axiosInstance = axios.create({
      timeout: this.config.timeout,
      headers: {
        'Accept': 'application/json',
      }
    });

    this.featureCache = new Map();
    
    this.searchEngine = new FuzzySearchEngine({
      mode: 'fuzzy',
      limit: 5,
      threshold: 0.2,
    });
  }

  /**
   * Configuración por defecto
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      wfsEndpoint: 'https://sedeaplicaciones.minetur.gob.es/ServiciosRESTCarburantes/PreciosCarburantes',
      layerName: 'EstacionesTerrestres',
      fuzzyThreshold: 0.35,
      timeout: 20000,
      outputSRS: 'EPSG:25830'
    };
  }

  /**
   * Obtiene código de provincia por nombre
   */
  private getProvinceCode(province: string): string | undefined {
    const normalized = province.toLowerCase().trim();
    return ANDALUCIA_PROVINCE_CODES[normalized] || undefined;
  }

  /**
   * Convierte coordenadas WGS84 a UTM30 ETRS89
   */
  private wgs84ToUtm30(lon: number, lat: number): [number, number] {
    try {
      const [x, y] = proj4(WGS84, UTM30, [lon, lat]);
      return [x, y];
    } catch {
      console.warn(`Error convirtiendo coordenadas WGS84: ${lon}, ${lat}`);
      return [0, 0];
    }
  }

  /**
   * Parsea respuesta JSON de la API MITECO
   */
  protected parseResponse(data: FuelStationAPIResponse): FuelStationFeature[] {
    const features: FuelStationFeature[] = [];

    // Estructura esperada: { ListaEESSPrecio: [...] }
    const stations = data?.ListaEESSPrecio || data?.EstacionesTerrestres || [];

    if (!Array.isArray(stations)) {
      console.warn('Respuesta sin array de estaciones:', typeof data);
      return features;
    }

    for (const station of stations) {
      const feature = this.parseStation(station);
      if (feature) {
        features.push(feature);
      }
    }

    return features;
  }

  /**
   * Parsea una estación individual
   */
  private parseStation(station: FuelStationAPIRecord): FuelStationFeature | null {
    try {
      // Extraer coordenadas (formato español: coma decimal)
      const latStr = station['Latitud'] || station['latitud'] || '';
      const lonStr = station['Longitud (WGS84)'] || station['longitud'] || '';

      // Convertir formato español (coma) a número
      const lat = parseFloat(latStr.replace(',', '.'));
      const lon = parseFloat(lonStr.replace(',', '.'));

      if (isNaN(lat) || isNaN(lon) || lat === 0 || lon === 0) {
        return null;
      }

      // Validar que está en España peninsular
      if (lat < 35 || lat > 44 || lon < -10 || lon > 5) {
        return null;
      }

      // Convertir a UTM30
      const [x, y] = this.wgs84ToUtm30(lon, lat);

      // Validar coordenadas UTM30 Andalucía
      if (x < 100000 || x > 800000 || y < 4000000 || y > 4300000) {
        return null;
      }

      // Extraer datos
      const idEESS = station['IDEESS'] || station['ideess'] || '';
      const rotulo = station['Rótulo'] || station['rotulo'] || '';
      const direccion = station['Dirección'] || station['direccion'] || '';
      const municipio = station['Municipio'] || station['municipio'] || '';
      const provincia = station['Provincia'] || station['provincia'] || '';
      const cp = station['C.P.'] || station['cp'] || '';
      const horario = station['Horario'] || station['horario'] || '';
      const tipoVenta = station['Tipo Venta'] || station['tipoVenta'] || '';
      const margen = station['Margen'] || station['margen'] || '';
      const localidad = station['Localidad'] || station['localidad'] || '';
      const idMunicipio = station['IDMunicipio'] || station['idMunicipio'] || '';

      // Detectar tipos de combustible disponibles
      const hasGLP = this.hasPrice(station, ['GLP', 'Precio Gases licuados del petróleo']);
      const hasGNC = this.hasPrice(station, ['GNC', 'Gas Natural Comprimido', 'Precio Gas Natural Comprimido']);
      const hasGNL = this.hasPrice(station, ['GNL', 'Gas Natural Licuado', 'Precio Gas Natural Licuado']);
      const hasHydrogen = this.hasPrice(station, ['Hidrógeno', 'Hidrogeno', 'Precio Hidrógeno']);
      // Nota: API MITECO no tiene campo explícito para carga eléctrica

      // Inferir tipo de estación
      const stationType = this.inferStationType({
        rotulo,
        hasGLP,
        hasGNC,
        hasGNL,
        hasHydrogen
      });

      // Construir nombre descriptivo
      const name = this.buildStationName(rotulo, direccion, municipio);

      return {
        id: idEESS,
        name,
        x,
        y,
        municipality: municipio,
        province: provincia,
        address: direccion,
        properties: {
          type: stationType,
          rotulo,
          postalCode: cp,
          horario,
          tipoVenta,
          hasGLP,
          hasGNC,
          hasGNL,
          hasHydrogen,
          margen,
          localidad,
          idMunicipio,
          latWGS84: lat,
          lonWGS84: lon
        }
      };
    } catch (error) {
      console.error('Error parseando estación:', error);
      return null;
    }
  }

  /**
   * Verifica si hay precio para un tipo de combustible
   */
  private hasPrice(station: FuelStationAPIRecord, keys: string[]): boolean {
    for (const key of keys) {
      const value = station[key] || station[`Precio ${key}`];
      if (value && value !== '' && value !== '0' && value !== '0,000') {
        return true;
      }
    }
    return false;
  }

  /**
   * Infiere el tipo de estación según combustibles disponibles
   */
  private inferStationType(info: {
    rotulo: string;
    hasGLP: boolean;
    hasGNC: boolean;
    hasGNL: boolean;
    hasHydrogen: boolean;
  }): FuelStationType {
    const { rotulo, hasGLP, hasGNC, hasGNL, hasHydrogen } = info;
    const rotuloLower = rotulo.toLowerCase();

    // Hidrógeno es muy específico
    if (hasHydrogen) {
      return FuelStationType.HIDROGENO;
    }

    // Electrolineras
    if (rotuloLower.includes('electr') || rotuloLower.includes('tesla') ||
        rotuloLower.includes('iberdrola') || rotuloLower.includes('endesa') ||
        rotuloLower.includes('ionity')) {
      return FuelStationType.ELECTROLINERA;
    }

    // GNL tiene prioridad (camiones)
    if (hasGNL) {
      return FuelStationType.GNL;
    }

    // GNC
    if (hasGNC) {
      return FuelStationType.GNC;
    }

    // GLP (Autogas)
    if (hasGLP) {
      return FuelStationType.GLP;
    }

    // Multiservicio si tiene varios tipos
    const count = [hasGLP, hasGNC, hasGNL].filter(Boolean).length;
    if (count >= 2) {
      return FuelStationType.MULTISERVICIO;
    }

    return FuelStationType.GASOLINERA;
  }

  /**
   * Construye nombre descriptivo para la estación
   */
  private buildStationName(rotulo: string, direccion: string, municipio: string): string {
    const parts: string[] = [];
    
    if (rotulo && rotulo.trim()) {
      parts.push(rotulo.trim());
    }
    
    if (direccion && direccion.trim()) {
      // Extraer parte significativa de la dirección
      const shortDir = direccion.split(',')[0].trim();
      if (shortDir && !parts[0]?.toLowerCase().includes(shortDir.toLowerCase())) {
        parts.push(shortDir);
      }
    }

    if (municipio && municipio.trim() && parts.length < 2) {
      parts.push(municipio.trim());
    }

    return parts.join(' - ') || 'Estación de Servicio';
  }

  /**
   * Pre-indexa features de una provincia para búsquedas fuzzy rápidas
   */
  protected indexProvinceFeatures(
    province: string,
    features: FuelStationFeature[]
  ): number {
    const normalizedProvince = province.toLowerCase().trim();
    
    if (this.currentIndexProvince === normalizedProvince) {
      return this.indexedFeatures.length;
    }
    
    this.indexedFeatures = features;
    this.currentIndexProvince = normalizedProvince;
    
    const searchableItems: SearchableItem[] = features.map((feature, idx) => ({
      id: String(idx),
      text: feature.name || '',
      metadata: {
        x: feature.x,
        y: feature.y,
        municipality: feature.municipality,
        province: feature.province,
        address: feature.address,
        properties: feature.properties,
      },
    }));
    
    return this.searchEngine.index(searchableItems);
  }

  /**
   * Geocodifica una estación de servicio
   */
  public async geocode(options: FuelStationSearchOptions): Promise<TypedResult<GeocodedCoordinates>> {
    const startTime = Date.now();
    
    const errorContext = createErrorContext('rest_miteco', 'L1', {
      elementName: options.name,
      municipio: options.municipality,
      provincia: options.province,
    });
    
    try {
      // Obtener estaciones de la provincia
      const features = await this.fetchStationsByProvince(options.province || '', options);

      if (features.length === 0) {
        return failure(
          createError.noResults(
            errorContext,
            options.name,
            { municipality: options.municipality || '', province: options.province || '' },
            true
          )
        );
      }

      // Pre-indexar
      if (options.province) {
        this.indexProvinceFeatures(options.province, features);
      }

      // Filtrar por municipio si se especifica
      let filteredFeatures = features;
      if (options.municipality) {
        const muniLower = options.municipality.toLowerCase();
        filteredFeatures = features.filter(f => 
          f.municipality?.toLowerCase().includes(muniLower) ||
          f.properties.localidad?.toLowerCase().includes(muniLower)
        );

        // Si no hay matches exactos, usar fuzzy en el municipio
        if (filteredFeatures.length === 0) {
          filteredFeatures = features;
        }
      }

      // Aplicar fuzzy matching
      const bestMatch = this.findBestMatch(options.name, filteredFeatures);

      if (!bestMatch || bestMatch.score < this.config.fuzzyThreshold) {
        return failure(
          createError.lowConfidence(
            errorContext,
            bestMatch?.score ? Math.round(bestMatch.score * 100) : 0,
            Math.round(this.config.fuzzyThreshold * 100),
            [{ factor: 'fuzzy_match', contribution: -(1 - (bestMatch?.score || 0)) * 100 }]
          )
        );
      }

      const legacyResult = this.featureToGeocodingResult(bestMatch.feature, bestMatch.score, options.name);
      
      return success(this.legacyToTyped(legacyResult), {
        processingTime: Date.now() - startTime,
        sourcesConsulted: ['rest_miteco'],
        finalSource: 'rest_miteco',
        cascadeLevel: 'L1',
        fromCache: false,
      });

    } catch (error) {
      const axiosError = error as AxiosError;
      
      if (axiosError.code === 'ECONNABORTED' || axiosError.message?.includes('timeout')) {
        return failure(
          createError.timeout(errorContext, this.config.wfsEndpoint, this.config.timeout)
        );
      }
      
      if (axiosError.response?.status === 429) {
        return failure(
          createError.rateLimit(errorContext, this.config.wfsEndpoint, 60)
        );
      }
      
      if (axiosError.response?.status && axiosError.response.status >= 500) {
        return failure(
          createError.serviceUnavailable(errorContext, this.config.wfsEndpoint, axiosError.response.status)
        );
      }
      
      if (axiosError.message?.includes('CORS') || axiosError.message?.includes('Network')) {
        return failure(
          createError.cors(errorContext, this.config.wfsEndpoint)
        );
      }
      
      return failure(
        createError.connection(errorContext, this.config.wfsEndpoint, 'unknown')
      );
    }
  }

  /**
   * Obtiene estaciones por provincia usando caché
   */
  private async fetchStationsByProvince(
    province: string,
    options: FuelStationSearchOptions
  ): Promise<FuelStationFeature[]> {
    // Intentar obtener código de provincia
    let provinceCode = options.provinceCode;
    
    if (!provinceCode && province) {
      provinceCode = this.getProvinceCode(province);
    }

    // Cache key
    const cacheKey = provinceCode || 'andalucia';

    // Revisar caché
    if (this.featureCache.has(cacheKey)) {
      return this.featureCache.get(cacheKey)!;
    }

    // Construir URL
    let url: string;
    if (provinceCode) {
      url = `${this.config.wfsEndpoint}/EstacionesTerrestres/FiltroProvincia/${provinceCode}`;
    } else {
      // Filtrar por toda Andalucía
      url = `${this.config.wfsEndpoint}/EstacionesTerrestres/FiltroCCAA/${CCAA_ANDALUCIA}`;
    }

    try {
      const response = await this.axiosInstance.get(url);
      const features = this.parseResponse(response.data);

      // Guardar en caché (TTL: 24h implícito por sesión)
      this.featureCache.set(cacheKey, features);

      return features;
    } catch (error) {
      console.warn(`Error obteniendo estaciones de ${cacheKey}:`, error);
      throw error;
    }
  }

  /**
   * Convierte resultado legacy a formato tipado
   */
  private legacyToTyped(legacy: LegacyGeocodingResult): GeocodedCoordinates {
    return {
      x: legacy.x,
      y: legacy.y,
      confidence: legacy.confidence,
      source: legacy.source,
      sourceLayer: legacy.sourceLayer,
      matchedName: legacy.matchedName,
      matchType: legacy.matchType,
      fuzzyScore: legacy.fuzzyScore,
      address: legacy.address,
      municipality: legacy.municipality,
      muniCode: legacy.muniCode,
      province: legacy.province,
      metadata: legacy.metadata,
    };
  }

  /**
   * Geocodifica con firma legacy
   */
  public async geocodeLegacy(options: FuelStationSearchOptions): Promise<LegacyGeocodingResult | null> {
    const result = await this.geocode(options);
    if (result.success) {
      return result.data as unknown as LegacyGeocodingResult;
    }
    return null;
  }

  /**
   * Encuentra el mejor match usando FuzzySearchEngine
   */
  protected findBestMatch(
    searchName: string, 
    features: FuelStationFeature[]
  ): { feature: FuelStationFeature; score: number } | null {
    
    if (features.length === 0) {
      return null;
    }

    // Indexación on-the-fly si es necesario
    if (!this.searchEngine.isReady || this.indexedFeatures !== features) {
      this.indexedFeatures = features;
      const searchableItems: SearchableItem[] = features.map((feature, idx) => ({
        id: String(idx),
        text: feature.name || '',
        metadata: { featureIndex: idx },
      }));
      this.searchEngine.index(searchableItems);
    }

    const result = this.searchEngine.searchBest(searchName, 0.2);

    if (!result) {
      return null;
    }

    const featureIndex = parseInt(result.item.id, 10);
    const feature = this.indexedFeatures[featureIndex];

    if (!feature) {
      return null;
    }

    return {
      feature,
      score: result.score,
    };
  }

  /**
   * Convierte FuelStationFeature a GeocodingResult
   */
  protected featureToGeocodingResult(
    feature: FuelStationFeature,
    fuzzyScore: number,
    _originalName: string
  ): LegacyGeocodingResult {
    return {
      x: feature.x,
      y: feature.y,
      confidence: Math.round(fuzzyScore * 100),
      source: 'MITECO-Gasolineras',
      sourceLayer: 'EstacionesTerrestres',
      matchedName: feature.name,
      fuzzyScore,
      address: feature.address,
      municipality: feature.municipality || '',
      muniCode: feature.properties.idMunicipio,
      province: feature.province || '',
      metadata: {
        stationType: feature.properties.type,
        rotulo: feature.properties.rotulo,
        idEESS: feature.id,
        hasGLP: feature.properties.hasGLP,
        hasGNC: feature.properties.hasGNC,
        hasGNL: feature.properties.hasGNL,
        hasHydrogen: feature.properties.hasHydrogen,
        horario: feature.properties.horario
      }
    };
  }

  /**
   * Busca todas las gasolineras de un municipio
   */
  public async searchStationsInMunicipality(
    municipality: string,
    province?: string,
    stationType?: FuelStationType
  ): Promise<FuelStationFeature[]> {
    const features = await this.fetchStationsByProvince(province || '', {
      name: '',
      municipality,
      province
    });

    const muniLower = municipality.toLowerCase();
    let filtered = features.filter(f => 
      f.municipality?.toLowerCase().includes(muniLower) ||
      f.properties.localidad?.toLowerCase().includes(muniLower)
    );

    // Filtrar por tipo si se especifica
    if (stationType) {
      filtered = filtered.filter(f => f.properties.type === stationType);
    }

    return filtered;
  }

  /**
   * Busca estaciones con GLP
   */
  public async searchGLPStations(
    municipality: string,
    province?: string
  ): Promise<FuelStationFeature[]> {
    const features = await this.searchStationsInMunicipality(municipality, province);
    return features.filter(f => f.properties.hasGLP);
  }

  /**
   * Busca estaciones con GNC
   */
  public async searchGNCStations(
    municipality: string,
    province?: string
  ): Promise<FuelStationFeature[]> {
    const features = await this.searchStationsInMunicipality(municipality, province);
    return features.filter(f => f.properties.hasGNC);
  }

  /**
   * Busca estaciones con GNL (para camiones)
   */
  public async searchGNLStations(
    municipality: string,
    province?: string
  ): Promise<FuelStationFeature[]> {
    const features = await this.searchStationsInMunicipality(municipality, province);
    return features.filter(f => f.properties.hasGNL);
  }

  /**
   * Obtiene estadísticas del geocodificador
   */
  public getStats(): { 
    cacheSize: number; 
    endpoint: string; 
    searchIndexSize: number;
    currentIndexProvince: string | null;
  } {
    return {
      cacheSize: this.featureCache.size,
      endpoint: this.config.wfsEndpoint,
      searchIndexSize: this.searchEngine.size,
      currentIndexProvince: this.currentIndexProvince,
    };
  }

  /**
   * Limpia caché y búsqueda
   */
  public clearCache(): void {
    this.featureCache.clear();
    this.searchEngine.clear();
    this.currentIndexProvince = null;
    this.indexedFeatures = [];
  }
}

export default GasolinerasGeocoder;
